
import React, { useMemo, useState } from "react";
import {
  Users,
  UserCheck,
  UserX,
  ShieldCheck,
  Search,
  Plus,
  Eye,
  Pencil,
  Trash2,
  Power,
  X,
  Mail,
  Phone,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  RotateCcw
} from "lucide-react";

const initialStaffs = [
  {
    id: 1,
    staffId: "STF001",
    name: "Nandha Kumar",
    email: "admin@salemkongu.com",
    mobile: "9876543210",
    role: "Super Admin",
    status: "Active",
    joinedDate: "2026-04-01",
  },
  {
    id: 2,
    staffId: "STF002",
    name: "Arun Kumar",
    email: "arun@salemkongu.com",
    mobile: "9876543211",
    role: "Staff Admin",
    status: "Active",
    joinedDate: "2026-04-10",
  },
  {
    id: 3,
    staffId: "STF003",
    name: "Priya Devi",
    email: "priya@salemkongu.com",
    mobile: "9876543212",
    role: "Staff",
    status: "Active",
    joinedDate: "2026-05-05",
  },
  {
    id: 4,
    staffId: "STF004",
    name: "Karthik Raj",
    email: "karthik@salemkongu.com",
    mobile: "9876543213",
    role: "Staff",
    status: "Inactive",
    joinedDate: "2026-06-15",
  },
  {
    id: 5,
    staffId: "STF005",
    name: "Divya Lakshmi",
    email: "divya@salemkongu.com",
    mobile: "9876543214",
    role: "Staff Admin",
    status: "Active",
    joinedDate: "2026-07-01",
  },
];

const emptyForm = {
  name: "",
  email: "",
  mobile: "",
  role: "Staff",
  password: "",
};

function formatDate(value) {
  if (!value) return "—";

  return new Date(`${value}T00:00:00`).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function RoleBadge({ role }) {
  const styles = {
    "Super Admin": "bg-violet-50 text-violet-700",
    "Staff Admin": "bg-blue-50 text-blue-700",
    Staff: "bg-slate-100 text-slate-600",
  };

  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[10px] font-semibold ${
        styles[role] || styles.Staff
      }`}
    >
      {role}
    </span>
  );
}

function StatusBadge({ status }) {
  const active = status === "Active";

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold ${
        active
          ? "bg-emerald-50 text-emerald-700"
          : "bg-slate-100 text-slate-600"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          active ? "bg-emerald-500" : "bg-slate-400"
        }`}
      />
      {status}
    </span>
  );
}

function StatCard({ title, value, icon: Icon, color, description }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    violet: "bg-violet-50 text-violet-600",
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-slate-500">{title}</p>
          <h3 className="mt-2 text-2xl font-bold text-slate-800">
            {value}
          </h3>
          <p className="mt-2 text-[11px] text-slate-400">{description}</p>
        </div>

        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${colors[color]}`}
        >
          <Icon size={21} />
        </div>
      </div>
    </div>
  );
}

function StaffModal({ staff, onClose, onSave }) {
  const [form, setForm] = useState(() =>
    staff
      ? {
          name: staff.name,
          email: staff.email,
          mobile: staff.mobile,
          role: staff.role,
          password: "",
        }
      : emptyForm
  );

  const [error, setError] = useState("");

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setError("");
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!/^\d{10}$/.test(form.mobile)) {
      setError("Enter a valid 10-digit mobile number.");
      return;
    }

    if (!staff && form.password.length < 8) {
      setError("Password must contain at least 8 characters.");
      return;
    }

    onSave({
      name: form.name.trim(),
      email: form.email.trim(),
      mobile: form.mobile,
      role: form.role,
    });
  };

  const inputClass =
    "mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100";

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-slate-900/50 p-3 sm:p-5">
      <div className="my-auto w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-6">
          <div>
            <h2 className="text-lg font-bold text-slate-800">
              {staff ? "Edit Staff Details" : "Add New Staff"}
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Enter staff contact information and assign a role.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
          >
            <X size={19} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 p-5 sm:p-6">
            <div>
              <label className="text-xs font-semibold text-slate-600">
                Full Name *
              </label>
              <input
                required
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                placeholder="Enter staff name"
                className={inputClass}
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600">
                Email Address *
              </label>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => updateField("email", e.target.value)}
                placeholder="staff@example.com"
                className={inputClass}
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600">
                Mobile Number *
              </label>
              <input
                required
                type="tel"
                inputMode="numeric"
                pattern="[0-9]{10}"
                maxLength={10}
                value={form.mobile}
                onChange={(e) =>
                  updateField(
                    "mobile",
                    e.target.value.replace(/\D/g, "").slice(0, 10)
                  )
                }
                placeholder="10-digit mobile number"
                className={inputClass}
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600">
                Assign Role *
              </label>
              <select
                required
                value={form.role}
                onChange={(e) => updateField("role", e.target.value)}
                className={inputClass}
              >
                <option value="Staff">Staff</option>
                <option value="Staff Admin">Staff Admin</option>
                <option value="Super Admin">Super Admin</option>
              </select>
              <p className="mt-1.5 text-[10px] text-slate-400">
                Role permissions will be configured in the Assign Roles module.
              </p>
            </div>

            {!staff && (
              <div>
                <label className="text-xs font-semibold text-slate-600">
                  Temporary Password *
                </label>
                <input
                  required
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  value={form.password}
                  onChange={(e) =>
                    updateField("password", e.target.value)
                  }
                  placeholder="Minimum 8 characters"
                  className={inputClass}
                />
                <p className="mt-1.5 text-[10px] text-slate-400">
                  Demo only. In production, create accounts through the backend
                  and store only securely hashed passwords.
                </p>
              </div>
            )}

            {error && (
              <p
                role="alert"
                className="rounded-lg bg-rose-50 px-3 py-2.5 text-xs text-rose-700"
              >
                {error}
              </p>
            )}
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-slate-100 bg-slate-50/70 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
            >
              {staff ? "Save Changes" : "Create Staff"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StaffDetailsModal({ staff, onClose, onEdit }) {
  if (!staff) return null;

  const details = [
    ["Staff ID", staff.staffId],
    ["Full Name", staff.name],
    ["Email Address", staff.email],
    ["Mobile Number", staff.mobile],
    ["Assigned Role", staff.role],
    ["Account Status", staff.status],
    ["Joined Date", formatDate(staff.joinedDate)],
  ];

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-100 p-5">
          <div>
            <h2 className="font-bold text-slate-800">Staff Details</h2>
            <p className="mt-1 text-xs text-slate-500">{staff.staffId}</p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close details"
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
          >
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[75vh] overflow-y-auto p-5">
          <div className="flex items-center gap-3 rounded-xl bg-blue-50 p-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 text-xl font-bold text-blue-700">
              {staff.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-slate-800">{staff.name}</h3>
              <p className="mt-1 break-all text-xs text-slate-500">
                {staff.email}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <RoleBadge role={staff.role} />
                <StatusBadge status={staff.status} />
              </div>
            </div>
          </div>

          <div className="mt-4 divide-y divide-slate-100">
            {details.map(([label, value]) => (
              <div
                key={label}
                className="flex items-start justify-between gap-4 py-3"
              >
                <span className="text-xs text-slate-500">{label}</span>
                <span className="break-all text-right text-xs font-semibold text-slate-700">
                  {value}
                </span>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() => onEdit(staff)}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
          >
            <Pencil size={15} />
            Edit Staff
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Staffs() {
  const [staffs, setStaffs] = useState(initialStaffs);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [viewingStaff, setViewingStaff] = useState(null);
  const [deleteStaff, setDeleteStaff] = useState(null);
  const [notice, setNotice] = useState("");

  const stats = useMemo(
    () => [
      {
        title: "Total Staff",
        value: staffs.length,
        icon: Users,
        color: "blue",
        description: "All registered staff",
      },
      {
        title: "Active Staff",
        value: staffs.filter((s) => s.status === "Active").length,
        icon: UserCheck,
        color: "green",
        description: "Currently enabled",
      },
      {
        title: "Inactive Staff",
        value: staffs.filter((s) => s.status === "Inactive").length,
        icon: UserX,
        color: "amber",
        description: "Currently disabled",
      },
      {
        title: "Administrators",
        value: staffs.filter((s) => s.role !== "Staff").length,
        icon: ShieldCheck,
        color: "violet",
        description: "Super Admin and Staff Admin",
      },
    ],
    [staffs]
  );

  const filteredStaffs = useMemo(() => {
    const term = search.trim().toLowerCase();

    return staffs.filter((staff) => {
      const matchesSearch =
        !term ||
        staff.name.toLowerCase().includes(term) ||
        staff.email.toLowerCase().includes(term) ||
        staff.mobile.includes(term) ||
        staff.staffId.toLowerCase().includes(term);

      return (
        matchesSearch &&
        (!roleFilter || staff.role === roleFilter) &&
        (!statusFilter || staff.status === statusFilter)
      );
    });
  }, [staffs, search, roleFilter, statusFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredStaffs.length / pageSize)
  );
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const visibleStaffs = filteredStaffs.slice(
    startIndex,
    startIndex + pageSize
  );

  const resetFilters = () => {
    setSearch("");
    setRoleFilter("");
    setStatusFilter("");
    setPage(1);
  };

  const saveStaff = (form) => {
    if (editingStaff) {
      setStaffs((current) =>
        current.map((staff) =>
          staff.id === editingStaff.id ? { ...staff, ...form } : staff
        )
      );
      setNotice("Staff details updated successfully.");
    } else {
      const nextNumber =
        Math.max(
          0,
          ...staffs.map(
            (staff) =>
              Number(staff.staffId.replace("STF", "")) || 0
          )
        ) + 1;

      setStaffs((current) => [
        {
          ...form,
          id: Date.now(),
          staffId: `STF${String(nextNumber).padStart(3, "0")}`,
          status: "Active",
          joinedDate: new Date().toISOString().slice(0, 10),
        },
        ...current,
      ]);

      setNotice("Staff created successfully.");
      setPage(1);
    }

    setModalOpen(false);
    setEditingStaff(null);
  };

  const toggleStatus = (staff) => {
    const nextStatus = staff.status === "Active" ? "Inactive" : "Active";

    setStaffs((current) =>
      current.map((item) =>
        item.id === staff.id ? { ...item, status: nextStatus } : item
      )
    );

    setNotice(`${staff.name} is now ${nextStatus.toLowerCase()}.`);
  };

  const confirmDelete = () => {
    setStaffs((current) =>
      current.filter((staff) => staff.id !== deleteStaff.id)
    );
    setNotice("Staff removed from the sample list.");
    setDeleteStaff(null);
  };

  const openEdit = (staff) => {
    setViewingStaff(null);
    setEditingStaff(staff);
    setModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Heading */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2 text-[11px] text-slate-400">
            <span>Home</span>
            <span>/</span>
            <span>Management</span>
            <span>/</span>
            <span className="font-medium text-blue-600">Staffs</span>
          </div>

          <h1 className="text-xl font-bold text-slate-800 sm:text-2xl">
            Staff Management
          </h1>

          <p className="mt-1 text-xs text-slate-500 sm:text-sm">
            Manage staff accounts, roles and account access.
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditingStaff(null);
            setModalOpen(true);
          }}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-xs font-semibold text-white shadow-sm hover:bg-blue-700"
        >
          <Plus size={17} />
          Add Staff
        </button>
      </div>

      {/* Notification */}
      {notice && (
        <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          <span>{notice}</span>
          <button
            type="button"
            onClick={() => setNotice("")}
            aria-label="Dismiss notification"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((item) => (
          <StatCard key={item.title} {...item} />
        ))}
      </div>

      {/* Search and filters */}
      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="mb-4">
          <h2 className="text-sm font-bold text-slate-800">
            Search & Filters
          </h2>
          <p className="mt-1 text-xs text-slate-400">
            Search by staff name, email or mobile number.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Name, staff ID, email or mobile"
              className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-xs outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-600 outline-none focus:border-blue-400"
          >
            <option value="">All Roles</option>
            <option value="Super Admin">Super Admin</option>
            <option value="Staff Admin">Staff Admin</option>
            <option value="Staff">Staff</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-600 outline-none focus:border-blue-400"
          >
            <option value="">All Account Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
          <p className="text-xs text-slate-500">
            Found{" "}
            <strong className="text-slate-800">
              {filteredStaffs.length}
            </strong>{" "}
            staff member(s)
          </p>

          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
          >
            <RotateCcw size={14} />
            Reset Filters
          </button>
        </div>
      </section>

      {/* Staff list */}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div>
            <h2 className="text-sm font-bold text-slate-800">
              Registered Staff
            </h2>
            <p className="mt-1 text-[11px] text-slate-400">
              Staff accounts and assigned roles
            </p>
          </div>

          <div className="flex items-center gap-2 text-xs text-slate-500">
            Show
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="rounded-md border border-slate-200 bg-white px-2 py-1.5"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
            </select>
            entries
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[950px] text-left">
            <thead className="bg-slate-50">
              <tr className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3">Staff Member</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Assigned Role</th>
                <th className="px-4 py-3">Joined Date</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {visibleStaffs.map((staff) => (
                <tr
                  key={staff.id}
                  className="transition hover:bg-slate-50/70"
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-50 text-sm font-bold text-blue-600">
                        {staff.name.charAt(0).toUpperCase()}
                      </div>

                      <div>
                        <p className="whitespace-nowrap text-xs font-semibold text-slate-800">
                          {staff.name}
                        </p>
                        <p className="mt-1 text-[10px] text-blue-600">
                          {staff.staffId}
                        </p>
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <Mail size={13} className="shrink-0 text-slate-400" />
                      <span>{staff.email}</span>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500">
                      <Phone size={12} className="shrink-0 text-slate-400" />
                      {staff.mobile}
                    </div>
                  </td>

                  <td className="whitespace-nowrap px-4 py-4">
                    <RoleBadge role={staff.role} />
                  </td>

                  <td className="whitespace-nowrap px-4 py-4">
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <CalendarDays size={14} className="text-slate-400" />
                      {formatDate(staff.joinedDate)}
                    </div>
                  </td>

                  <td className="whitespace-nowrap px-4 py-4">
                    <button
                      type="button"
                      onClick={() => toggleStatus(staff)}
                      title="Toggle account status"
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-[10px] font-semibold transition ${
                        staff.status === "Active"
                          ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      <Power size={12} />
                      {staff.status}
                    </button>
                  </td>

                  <td className="whitespace-nowrap px-5 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        title="View staff"
                        aria-label={`View ${staff.name}`}
                        onClick={() => setViewingStaff(staff)}
                        className="rounded-lg p-2 text-blue-600 hover:bg-blue-50"
                      >
                        <Eye size={15} />
                      </button>

                      <button
                        type="button"
                        title="Edit staff"
                        aria-label={`Edit ${staff.name}`}
                        onClick={() => openEdit(staff)}
                        className="rounded-lg p-2 text-amber-600 hover:bg-amber-50"
                      >
                        <Pencil size={15} />
                      </button>

                      <button
                        type="button"
                        title="Delete staff"
                        aria-label={`Delete ${staff.name}`}
                        onClick={() => setDeleteStaff(staff)}
                        className="rounded-lg p-2 text-rose-600 hover:bg-rose-50"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {visibleStaffs.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-14 text-center">
                    <Users size={28} className="mx-auto text-slate-300" />
                    <p className="mt-3 text-sm font-semibold text-slate-700">
                      No staff found
                    </p>
                    <p className="mt-1 text-xs text-slate-400">
                      Change your search or filter criteria.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <p className="text-[11px] text-slate-500">
            Showing {filteredStaffs.length ? startIndex + 1 : 0} to{" "}
            {Math.min(startIndex + pageSize, filteredStaffs.length)} of{" "}
            {filteredStaffs.length} entries
          </p>

          <div className="flex items-center gap-1 self-end sm:self-auto">
            <button
              type="button"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-slate-200 p-2 text-slate-500 disabled:opacity-40"
              aria-label="Previous page"
            >
              <ChevronLeft size={15} />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (number) =>
                  number === 1 ||
                  number === totalPages ||
                  Math.abs(number - safePage) <= 1
              )
              .map((number, index, numbers) => (
                <React.Fragment key={number}>
                  {index > 0 && number - numbers[index - 1] > 1 && (
                    <span className="px-1 text-xs text-slate-400">...</span>
                  )}

                  <button
                    type="button"
                    onClick={() => setPage(number)}
                    className={`h-8 min-w-8 rounded-lg px-2 text-xs font-medium ${
                      safePage === number
                        ? "bg-blue-600 text-white"
                        : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {number}
                  </button>
                </React.Fragment>
              ))}

            <button
              type="button"
              disabled={safePage >= totalPages}
              onClick={() =>
                setPage((p) => Math.min(totalPages, p + 1))
              }
              className="rounded-lg border border-slate-200 p-2 text-slate-500 disabled:opacity-40"
              aria-label="Next page"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
      </section>

      {/* Add / Edit modal */}
      {modalOpen && (
        <StaffModal
          staff={editingStaff}
          onClose={() => {
            setModalOpen(false);
            setEditingStaff(null);
          }}
          onSave={saveStaff}
        />
      )}

      {/* View details modal */}
      {viewingStaff && (
        <StaffDetailsModal
          staff={viewingStaff}
          onClose={() => setViewingStaff(null)}
          onEdit={openEdit}
        />
      )}

      {/* Delete confirmation */}
      {deleteStaff && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-rose-600">
              <Trash2 size={22} />
            </div>

            <h3 className="mt-4 text-center text-lg font-bold text-slate-800">
              Delete Staff?
            </h3>

            <p className="mt-2 text-center text-sm text-slate-500">
              Are you sure you want to remove{" "}
              <strong>{deleteStaff.name}</strong> from the sample list?
            </p>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setDeleteStaff(null)}
                className="flex-1 rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={confirmDelete}
                className="flex-1 rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-rose-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
